import { AddSharp, DeleteForeverRounded } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import AcxButton from "components/UI/AcxButton";
import AcxDataGrid from "components/UI/AcxDataGrid/AcxDataGrid";
import { hierarchyColumnType } from "components/UI/AcxDataGrid/ColumnTypes/AcxGridColTypes";
import IColDef from "components/UI/AcxDataGrid/IColDef";
import { Observer, observer } from "mobx-react";
import React, { useEffect } from "react";
import { useNavigate } from "react-router";
import { AuthStore } from "stores/AuthStore";
import { parseFromISO, standardDateFormat } from "utils/DateTimeUtils";
import { useStore } from "utils/useStore";
import CreateNewAgentDialog from "./OrganizationAgentDetails/CreateNewAgentDialog";
import MergeAgentsDialog from "./OrganizationAgentDetails/MergeAgentsDialog";
import UpdateAgentsDialog from "./OrganizationAgentDetails/UpdateAgentDialog";
import { OrganizationAgentStore } from "./stores/OrganizationAgentStore";

type Props = {
    orgId: string;
    inActiveStatus?: any;
};

const OrganizationAgentsList: React.FC<
    React.PropsWithChildren<React.PropsWithChildren<Props>>
> = observer(({ orgId, inActiveStatus = true }) => {
    const navigate = useNavigate();
    const agentStore = useStore(OrganizationAgentStore);
    const authStore = useStore(AuthStore);

    const getCurrentDisplay = function () {
        if (inActiveStatus) {
            inActiveStatus = false;
            agentStore.loadInActiveAgents();
            agentStore.ActiveAgentsLabel = "Show Active Agents";
        } else {
            inActiveStatus = true;
            agentStore.loadActiveAgents();
            agentStore.ActiveAgentsLabel = "Show Inactive Agents";
        }
    };

    useEffect(() => {
        agentStore.dgStore.clearSelected();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        const columns: IColDef[] = [
            {
                headerName: "Agent Name",
                field: "fullName",
                width: 150,
            },
            {
                headerName: "First Name",
                field: "firstName",
                width: 150,
            },
            {
                headerName: "Last Name",
                field: "lastName",
                width: 150,
            },
            {
                headerName: "Email",
                field: "agentEmail",
                width: 250,
            },
            {
                headerName: "Service Access",
                field: "displayHierarchies",
                flex: 1,
                ...hierarchyColumnType,
            },
            {
                headerName: "Agent Code",
                field: "agentCode",
                width: 150,
            },
            {
                headerName: "Agent Status",
                field: "statusDisplay",
                width: 150,
            },
            {
                headerName: "Agent User",
                field: "user.userName",
                width: 250,
                valueGetter: (value, row, column, apiRef) =>
                    row.user.userName ? row.user.userName : "",
            },
            {
                headerName: "Agent Manager",
                field: "managerFullName",
                width: 150,
            },
            {
                headerName: "Start Date",
                field: "startDate",
                type: "date",
                // Possibly refactor to column definition
                valueGetter: (_, row, column) => {
                    return row.startDate ? new Date(row.startDate) : null;
                },
                renderCell: ({ value }) => {
                    return value
                        ? standardDateFormat(parseFromISO(value, true))
                        : "";
                },
                width: 150,
            },
        ];

        agentStore.orgId = orgId;
        agentStore.dgStore.setColumns(columns);
        agentStore.dgStore.removeHeight = "65px";
        agentStore.dgStore.controlsColumnStyle = {
            justifyContent: "flex-end",
            marginTop: "0.5rem",
        };
        agentStore.dgStore.controlsColumnSpan = 12;
        agentStore.ActiveAgentsLabel = "Show Inactive Agents";
        agentStore.dgStore.controls = [
            <Observer>
                {() => (
                    <div>
                        <AcxButton
                            fullWidth
                            color="primary"
                            onClick={getCurrentDisplay}
                            style={{
                                padding: "1rem",
                                minWidth: "max-content",
                            }}
                        >
                            {agentStore.ActiveAgentsLabel}
                        </AcxButton>
                    </div>
                )}
            </Observer>,
            <Observer>
                {() => (
                    <div>
                        <AcxButton
                            fullWidth
                            disabled={!authStore.canUserEdit("Agents")}
                            color="primary"
                            style={{
                                padding: "1rem",
                                minWidth: "max-content",
                            }}
                            startIcon={<AddSharp />}
                            onClick={agentStore.openCreateAgent}
                        >
                            Add
                        </AcxButton>
                    </div>
                )}
            </Observer>,

            // agent user button

            <Observer>
                {() => (
                    <div>
                        <AcxButton
                            fullWidth
                            style={{ height: "33px" }}
                            disabled={
                                !authStore.canUserEdit("Agents") ||
                                !agentStore.updateButtonEnabled
                            }
                            color="primary"
                            startIcon={<AddSharp />}
                            onClick={() => {
                                agentStore.createAgentUser();
                                navigate(
                                    `/admin/organizations/${orgId}/users/add`,
                                );
                            }}
                        >
                            Add Agent User
                        </AcxButton>
                    </div>
                )}
            </Observer>,

            <Observer>
                {() => (
                    <div>
                        <AcxButton
                            fullWidth
                            disabled={
                                !authStore.canUserEdit("Agents") ||
                                !agentStore.updateButtonEnabled
                            }
                            color="primary"
                            style={{
                                padding: "1rem",
                                minWidth: "max-content",
                            }}
                            startIcon={<EditIcon />}
                            onClick={agentStore.openUpdateAgent}
                        >
                            Update
                        </AcxButton>
                    </div>
                )}
            </Observer>,
            <Observer>
                {() => (
                    <div>
                        <AcxButton
                            fullWidth
                            disabled={
                                !authStore.canUserEdit("Agents") ||
                                !agentStore.deleteButtonEnabled
                            }
                            color="primary"
                            style={{
                                padding: "1rem",
                                minWidth: "max-content",
                            }}
                            startIcon={<DeleteForeverRounded />}
                            onClick={agentStore.deleteAgents}
                        >
                            Delete
                        </AcxButton>
                    </div>
                )}
            </Observer>,
            <Observer>
                {() => (
                    <div>
                        <AcxButton
                            fullWidth
                            disabled={
                                !authStore.canUserEdit("Agents") ||
                                !agentStore.mergeButtonEnabled
                            }
                            color="primary"
                            style={{
                                padding: "1rem",
                                minWidth: "max-content",
                            }}
                            onClick={agentStore.openMergeAgents}
                        >
                            Merge Agents
                        </AcxButton>
                    </div>
                )}
            </Observer>,
        ];

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId]);

    return (
        <>
            <CreateNewAgentDialog
                isOpen={agentStore.agentCreateDialogStore.isOpen}
                onClose={agentStore.agentCreateDialogStore.close}
                onSubmitHandler={agentStore.onAgentCreateConfirm}
                isLoading={agentStore.getTaskLoading("Create Agent")}
            />

            {agentStore.dgStore.selectedRows.length === 1 && <UpdateAgentsDialog />}

            <MergeAgentsDialog />

            <div style={{ height: "90vh", width: "70vw" }}>
                <AcxDataGrid dataGridStore={agentStore.dgStore} />
            </div>
        </>
    );
});

export default OrganizationAgentsList;
